<script setup lang="ts">
	import StringMethods from '~/utils/character'
	import type { IconLibProps } from '../tools/icon.vue'

	export type TabsContext = Partial<{
		wrapper: string
		container: string
		base: string
		list: Partial<{
			base: string
			background: string
			rounded: string
			shadow: string
			padding: string
			height: string
			width: string
			marker: Partial<{
				wrapper: string
				base: string
				background: string
				rounded: string
				shadow: string
			}>
			tab: Partial<{
				base: string
				background: string
				active: string
				inactive: string
				height: string
				padding: string
				size: string
				font: string
				maxWidth: string
				rounded: string
				shadow: string
			}>
		}>
	}>

	export type TabGroupContext = Array<{
		[id: string]: any
		label: string
		icon?: string
		content?: any
		iconProp?: IconLibProps
	}>
	const props = defineProps<{
		ui?: {
			tabProps: TabsContext
		}
		list: TabGroupContext | string[]
		value: any
	}>()
	const current = shallowRef(0)
	const { toLower } = StringMethods()

	const config: TabsContext = {
		wrapper: 'relative space-y-2',
		container: 'relative',
		base: 'focus:outline-none',
		list: {
			base: 'relative gap-x-2 sm:gap-x-4',
			background: '!bg-transparent',
			rounded: 'rounded-lg',
			shadow: '',
			padding: 'p-1',
			height: 'xs:h-10',
			width: ' ',
			marker: {
				wrapper: 'absolute top-[4px] left-[4px] duration-200 ease-out focus:outline-none',
				base: ' h-full',
				background: 'bg-main dark:bg-base-2',
				rounded: 'rounded-[40px]',
				shadow: 'shadow-sm'
			},
			tab: {
				base: 'relative inline-flex  items-center justify-center flex-shrink-0 ui-focus-visible:outline-0 ui-focus-visible:ring-2 ui-focus-visible:ring-primary-500 dark:ui-focus-visible:ring-primary-400 ui-not-focus-visible:outline-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors duration-200 ease-out sm:min-h-[56px] capitalize border border-main flex-none',
				background: '',
				active: 'text-white dark:text-white border-transparent',
				inactive: 'text-main dark:text-base-300 ',
				height: 'xs:h-8',
				padding: 'px-3 py-5',
				size: 'xs:text-sm text-xs',
				maxWidth: 'md:max-w-[180px]',
				font: 'font-semibold',
				rounded: 'rounded-[40px]',
				shadow: ''
			}
		}
	}

	const tabGroup = computed(() => {
		return (
			props?.list?.map(item => {
				if (typeof item !== 'object') {
					return {
						label: item,
						content: ''
					}
				}
				return item
			}) ?? []
		)
	})

	const emits = defineEmits<{
		(event: 'update:value', payload: string | number): void
		(event: 'change', payload: { item?: string; index?: number; content: TabGroupContext[number] | null }): void
	}>()

	const updateValue = (arg: number) => {
		const item = tabGroup.value.find((_, i) => i === arg) || null
		const label = item?.label || ''
		const value = item && 'value' in item ? item.value : label
		emits('update:value', value)
		emits('change', { item: label, index: current.value, content: item })
	}

	watch(
		() => props.value,
		val => {
			// Get Selected Item index
			if (tabGroup.value.length && val !== undefined && val !== null) {
				const index = tabGroup.value.findIndex(item => {
					const itemValue = item && typeof item === 'object' && 'value' in item ? item.value : item?.label || item
					return toLower(itemValue) === toLower(val)
				})
				if (index !== -1) {
					current.value = index
				}
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<div>
		<u-tabs
			:ui="{ ...config, ...props.ui?.tabProps }"
			v-model="current"
			:items="tabGroup"
			:default-index="0"
			@change="updateValue"
		>
			<template #default="content">
				<slot v-bind="content" />
			</template>
		</u-tabs>
	</div>
</template>
